// MapFloatingControls.jsx — the floating-control cluster (ticket 04 extraction): the shared
// FloatingControlShell plus BasemapControl, CoordGridControl, MovementTrailControl. Grouped
// into one file (rather than four) because they share the shell component and are always
// rendered together in the same `Stack` in MapEditorModalInner.

import React, { useCallback, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import GridOnRounded from '@mui/icons-material/GridOnRounded';
import LayersRounded from '@mui/icons-material/LayersRounded';
import RouteRounded from '@mui/icons-material/RouteRounded';

import { MAP_CONTROL_TRIGGER_SIZE } from '../../mapViewConstants';
import { BASEMAP_STYLES, OVERLAY_TYPES } from '../../schema/mapSchema';
import { OVERLAY_TYPE_OPTIONS, controlOptionRowSx, COORD_GRID_OPTIONS } from './mapEditorConstants';

// Ticket 01 (map-editor-modal-ux-audit): shared collapse/expand shell for the three floating
// map controls (BasemapControl / CoordGridControl / MovementTrailControl). Each renders as a
// single icon trigger by default and expands its option list in normal flex flow directly
// beneath the trigger (rather than an absolutely-positioned popover) — so the *stacking
// container* (see the `<Stack>` around all three in MapEditorModalInner) naturally grows/shrinks
// and its siblings shift with it, which is what guarantees the three controls' boxes never
// overlap at any combination of expanded/collapsed state, with no per-control offset math.
// Collapses again on outside click (ClickAwayListener) or Escape (stopPropagation so this
// doesn't also fire the modal-level Escape shortcut — see useEditorKeyboardShortcuts) or a
// re-click of the trigger (toggle).
export function FloatingControlShell({ icon, tooltip, ariaLabel, active, children }) {
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    const toggleOpen = useCallback(() => setOpen((v) => !v), []);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') { e.stopPropagation(); close(); }
    }, [close]);

    return (
        <ClickAwayListener onClickAway={close}>
            <Box onKeyDown={handleKeyDown} sx={(theme) => ({
                bgcolor: 'background.paper', borderRadius: 1, boxShadow: theme.shadows[2],
                border: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column', overflow: 'hidden',
            })}>
                <Tooltip title={tooltip} placement="left">
                    <IconButton
                        size="small"
                        onClick={toggleOpen}
                        aria-label={ariaLabel}
                        aria-expanded={open}
                        aria-pressed={active}
                        sx={{ width: MAP_CONTROL_TRIGGER_SIZE, height: MAP_CONTROL_TRIGGER_SIZE, borderRadius: 0 }}
                    >
                        {icon}
                    </IconButton>
                </Tooltip>
                {open && children}
            </Box>
        </ClickAwayListener>
    );
}

// rerender-memo: sibling floating controls re-render together whenever any of the trail/
// basemap/grid state changes above them in MapEditorModalInner — memoized like ContextPanel/
// MapDrawToolbar so an update to one control's props doesn't re-render the other two.
export const BasemapControl = React.memo(function BasemapControl({ current, onChange, overlayType, onOverlayTypeChange }) {
    return (
        <FloatingControlShell
            icon={<LayersRounded sx={{ fontSize: 18, color: 'text.secondary' }} />}
            tooltip="Basemap"
            ariaLabel="Basemap"
        >
            {OVERLAY_TYPE_OPTIONS.map((o) => (
                <ButtonBase
                    key={o.id}
                    onClick={() => onOverlayTypeChange(o.id)}
                    aria-pressed={overlayType === o.id}
                    sx={(theme) => controlOptionRowSx(theme, overlayType === o.id)}
                >
                    {o.label}
                </ButtonBase>
            ))}
            {overlayType !== OVERLAY_TYPES.SATELLITE && BASEMAP_STYLES.map((s) => (
                <ButtonBase
                    key={s.id}
                    onClick={() => onChange(s.url)}
                    aria-pressed={current === s.url}
                    sx={(theme) => controlOptionRowSx(theme, current === s.url)}
                >
                    {s.label}
                </ButtonBase>
            ))}
        </FloatingControlShell>
    );
});

export const CoordGridControl = React.memo(function CoordGridControl({ activeGrid, onToggle }) {
    return (
        <FloatingControlShell
            icon={<GridOnRounded sx={{ fontSize: 18, color: activeGrid ? 'primary.main' : 'text.disabled' }} />}
            tooltip="Coordinate grid"
            ariaLabel="Coordinate grid"
            active={!!activeGrid}
        >
            {COORD_GRID_OPTIONS.map((g) => (
                <ButtonBase
                    key={g}
                    onClick={() => onToggle(activeGrid === g.toLowerCase() ? null : g.toLowerCase())}
                    aria-pressed={activeGrid === g.toLowerCase()}
                    sx={(theme) => controlOptionRowSx(theme, activeGrid === g.toLowerCase())}
                >
                    {g}
                </ButtonBase>
            ))}
        </FloatingControlShell>
    );
});

// -- Ticket 05: "Show movement trail" global toggle + N-days window control ------------------
// Ticket 01 (map-editor-modal-ux-audit): now built on FloatingControlShell like its siblings —
// collapses to a single icon by default, in the same stacking column as BasemapControl/
// CoordGridControl (see the `<Stack>` in MapEditorModalInner) rather than a hand-tuned `bottom`
// offset. A global (not per-symbol) toggle per the ticket's explicit scope, plus a small numeric
// N-days input that only appears once the trail is enabled.
export const MovementTrailControl = React.memo(function MovementTrailControl({ enabled, onToggle, windowDays, onWindowDaysChange }) {
    // Ticket 04: last known-valid value, so onBlur can restore it if the field is left blank
    // or out of range. Seeded from the initial windowDays prop (7 by default — see the
    // `trailWindowDays` state declaration below) and updated whenever a valid value is typed.
    const lastValidRef = useRef(windowDays === '' || !Number.isFinite(windowDays) ? 7 : windowDays);

    const handleDaysChange = useCallback((e) => {
        const raw = e.target.value;
        if (raw === '') { onWindowDaysChange(''); return; }
        const n = Number(raw);
        if (!Number.isFinite(n)) return;
        // Per-keystroke: only clamp the min (as before) so typing "36" while aiming for "365"
        // is never force-corrected mid-entry. The 365 upper bound is enforced on blur instead.
        const clamped = Math.max(1, Math.round(n));
        lastValidRef.current = clamped;
        onWindowDaysChange(clamped);
    }, [onWindowDaysChange]);

    // Ticket 04: on blur, restore/clamp so the field never keeps showing blank or an
    // out-of-range value once focus leaves it.
    const handleDaysBlur = useCallback(() => {
        const n = Number(windowDays);
        if (windowDays === '' || !Number.isFinite(n) || n < 1) {
            onWindowDaysChange(lastValidRef.current);
            return;
        }
        const clamped = Math.min(365, Math.round(n));
        lastValidRef.current = clamped;
        if (clamped !== windowDays) onWindowDaysChange(clamped);
    }, [windowDays, onWindowDaysChange]);

    return (
        <FloatingControlShell
            icon={<RouteRounded sx={{ fontSize: 18, color: enabled ? 'primary.main' : 'text.disabled' }} />}
            tooltip="Movement trail"
            ariaLabel="Movement trail"
            active={enabled}
        >
            <Box sx={(theme) => ({
                width: 140, px: 1, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
            })}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: enabled ? 'primary.main' : 'text.disabled' }}>
                    Show trail
                </Typography>
                <Switch
                    size="small"
                    checked={enabled}
                    onChange={(e) => onToggle(e.target.checked)}
                    inputProps={{ 'aria-label': 'Show movement trail' }}
                />
            </Box>
            {enabled && (
                <Box sx={(theme) => ({
                    width: 140, px: 1, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    borderTop: `1px solid ${theme.palette.divider}`,
                })}>
                    <TextField
                        type="number"
                        size="small"
                        variant="standard"
                        value={windowDays}
                        onChange={handleDaysChange}
                        onBlur={handleDaysBlur}
                        inputProps={{ min: 1, max: 365, 'aria-label': 'Trail window (days)', style: { textAlign: 'center', fontSize: 13, padding: '2px 0' } }}
                        sx={{ width: 44 }}
                    />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: 'text.disabled' }}>DAYS</Typography>
                </Box>
            )}
        </FloatingControlShell>
    );
});
